import * as React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "lib/utils";
import { Skeleton } from "components/ui/skeleton";
import { tone, type Tone } from "components/ui/tone";

export type TrendDirection = "up" | "down" | "flat";

export interface StatCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onClick"> {
    /** Lucide component or any element type */
    icon?: React.ElementType;
    label: React.ReactNode;
    value: React.ReactNode;
    /** Secondary line under the value, e.g. "of 10,000 sent" */
    hint?: React.ReactNode;
    tone?: Tone;
    isLoading?: boolean;
    /** e.g. { direction: "up", value: "+12.4%", label: "vs last month" } */
    trend?: { direction: TrendDirection; value: string; label?: string; /** up is bad for e.g. bounce rate */ invert?: boolean };
    /** Renders the card as a button and wires keyboard activation */
    onClick?: () => void;
    /** Right-hand slot — sparkline, menu, etc. */
    aside?: React.ReactNode;
    /** Compact variant for dense toolbars/side rails */
    dense?: boolean;
}

const TREND_ICON: Record<TrendDirection, React.ElementType> = {
    up: TrendingUp,
    down: TrendingDown,
    flat: Minus,
};

function formatValue(value: React.ReactNode): React.ReactNode {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "number") {
        return Number.isFinite(value) ? value.toLocaleString() : "—";
    }
    return value;
}

/**
 * The canonical metric tile. Supersedes `PageKPICard`, `ModernKPICards` and
 * the per-feature KPI grids, all of which drew the same card with slightly
 * different padding, type sizes and colour props.
 */
export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
    (
        {
            icon: Icon,
            label,
            value,
            hint,
            tone: t = "brand",
            isLoading = false,
            trend,
            onClick,
            aside,
            dense = false,
            className,
            ...props
        },
        ref
    ) => {
        const tk = tone(t);
        const interactive = typeof onClick === "function";

        const TrendIcon = trend ? TREND_ICON[trend.direction] : null;
        // "good" means green: up is good unless the metric is inverted (bounce, unsubscribes)
        const trendIsGood =
            trend?.direction === "flat"
                ? null
                : trend
                    ? trend.invert
                        ? trend.direction === "down"
                        : trend.direction === "up"
                    : null;

        return (
            <div
                ref={ref}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={onClick}
                onKeyDown={
                    interactive
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onClick?.();
                            }
                        }
                        : undefined
                }
                className={cn(
                    "relative overflow-hidden rounded-xl bg-surface border border-line shadow-card",
                    "transition-[box-shadow,border-color,transform] duration-200 ease-ds w-full min-w-0",
                    interactive &&
                    "cursor-pointer hover:shadow-card-hover hover:border-line-strong hover:-translate-y-0.5 focus-ring",
                    className
                )}
                {...props}
            >
                {/* Accent rail — the tone's only full-strength appearance, so the
                    card stays quiet and the number stays the loudest element. */}
                <div className={cn("absolute top-0 inset-x-0 h-[2px]", tk.accent)} aria-hidden="true" />

                <div className={cn("min-w-0", dense ? "px-3 pt-3 pb-2.5" : "px-4 pt-4 pb-3.5")}>
                    <div className="flex items-start gap-2 mb-2.5 min-w-0">
                        {isLoading ? (
                            <Skeleton className="h-6 w-6 rounded-md flex-shrink-0" />
                        ) : Icon ? (
                            <span
                                className={cn(
                                    "flex-shrink-0 inline-flex items-center justify-center rounded-md",
                                    dense ? "w-5 h-5" : "w-6 h-6 sm:w-7 sm:h-7",
                                    tk.tile
                                )}
                                aria-hidden="true"
                            >
                                <Icon className={dense ? "w-3 h-3" : "w-3 h-3 sm:w-3.5 sm:h-3.5"} strokeWidth={2} />
                            </span>
                        ) : null}

                        {isLoading ? (
                            <Skeleton className="h-3.5 w-20 max-w-full rounded flex-shrink mt-1" />
                        ) : (
                            <span className="text-micro sm:text-meta font-semibold text-content-secondary line-clamp-2 leading-snug flex-1 min-w-0 mt-0.5">
                                {label}
                            </span>
                        )}

                        {aside && <div className="flex-shrink-0 -mt-0.5">{aside}</div>}
                    </div>

                    {isLoading ? (
                        <Skeleton className="h-7 w-14 max-w-full rounded" />
                    ) : (
                        <p
                            className={cn(
                                "font-bold text-content leading-none tracking-tight truncate tabular-nums",
                                dense ? "text-title-lg" : "text-display-sm"
                            )}
                        >
                            {formatValue(value)}
                        </p>
                    )}

                    {(trend || hint) && !isLoading && (
                        <div className="mt-2 flex items-center gap-2 min-w-0">
                            {trend && TrendIcon && (
                                <span
                                    className={cn(
                                        "inline-flex items-center gap-1 text-micro font-semibold tabular-nums",
                                        trendIsGood === null
                                            ? "text-content-muted"
                                            : trendIsGood
                                                ? "text-success"
                                                : "text-danger"
                                    )}
                                >
                                    <TrendIcon className="w-3 h-3" aria-hidden="true" />
                                    {trend.value}
                                </span>
                            )}
                            {(trend?.label || hint) && (
                                <span className="text-micro text-content-muted truncate min-w-0">
                                    {trend?.label ?? hint}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);
StatCard.displayName = "StatCard";

export interface StatCardGridProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Number of columns at the widest breakpoint. Below that it steps down. */
    columns?: 2 | 3 | 4 | 5 | 6;
}

const GRID: Record<number, string> = {
    2: "grid-cols-2",
    3: "grid-cols-2 sm:grid-cols-3",
    4: "grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
    6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
};

/** Responsive metric row. Two columns on phones so numbers stay readable. */
export const StatCardGrid: React.FC<StatCardGridProps> = ({
    columns = 4,
    className,
    children,
    ...props
}) => (
    <div className={cn("grid gap-3", GRID[columns] ?? GRID[4], className)} {...props}>
        {children}
    </div>
);

export default StatCard;
